import {
  createElement,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
} from "react";

const isBrowser = typeof window !== "undefined";

/**
 * LazyHydrate, render the children on the server (full SSR HTML), then on the
 * client skip hydrating the subtree until an IntersectionObserver reports the
 * wrapper is near the viewport. Once hydrated the children render normally.
 *
 * Pattern: on the client first render we emit the wrapper with
 * `dangerouslySetInnerHTML={{__html: ""}}` + `suppressHydrationWarning`. React
 * treats the existing SSR DOM as opaque and does not walk into it, so no
 * components mount, no effects run, no event listeners attach. When IO fires
 * we flip state, re-render with real children, and React reconciles the DOM
 * into the live tree.
 */
type LazyHydrateProps<T extends ElementType> = {
  as?: T;
  children: ReactNode;
  /** Pre-trigger margin so hydration starts just before the section enters view. */
  rootMargin?: string;
} & Omit<ComponentPropsWithoutRef<T>, "children" | "dangerouslySetInnerHTML">;

export function LazyHydrate<T extends ElementType = "div">({
  as,
  children,
  rootMargin = "200px",
  ...rest
}: LazyHydrateProps<T>) {
  const Tag = (as ?? "div") as ElementType;
  const ref = useRef<HTMLElement | null>(null);
  // SSR: hydrated=true so we emit the full markup.
  // Client first render: hydrated=false so we emit the empty-innerHTML stub.
  const [hydrated, setHydrated] = useState(!isBrowser);

  useEffect(() => {
    if (hydrated) return;
    const node = ref.current;
    if (!node) return;
    // No SSR children (e.g. conditional rendered empty) → nothing to defer.
    if (node.childElementCount === 0) {
      setHydrated(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setHydrated(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hydrated, rootMargin]);

  if (hydrated) {
    return createElement(
      Tag,
      { ...rest, ref, suppressHydrationWarning: true },
      children,
    );
  }

  return createElement(Tag, {
    ...rest,
    ref,
    suppressHydrationWarning: true,
    dangerouslySetInnerHTML: { __html: "" },
  });
}